import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Pagination from "@mui/material/Pagination";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters } from "/imports/libs/url";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import { UrlTabs, EmptyState, CheckInDialog, InstallAppCard, InviteQrCard } from "/imports/ui/components/shared";

import { ACTIVITY_TABS } from "./dashboardConstants";
import { formatMoney } from "./dashboardHelpers";
import OverviewTab from "./components/OverviewTab";
import SectionMenu from "./components/SectionMenu";
import SportsList from "./components/SportsList";
import ActivityList from "./components/ActivityList";
import BookingFilters from "./components/BookingFilters";
import InvoiceFilters from "./components/InvoiceFilters";
import PreferencesTab from "./components/PreferencesTab";
import CancelBookingDialog from "./components/CancelBookingDialog";
import InvoiceDetailsDialog from "./components/InvoiceDetailsDialog";
import PlanUsageDialog from "./components/PlanUsageDialog";
import CollapsibleSection from "./components/CollapsibleSection";
import SettlePaymentDialog from "./components/SettlePaymentDialog";
import RescheduleDialog from "./components/RescheduleDialog";
import WaitlistRow from "./components/WaitlistRow";
import { bookingRowKey } from "./dashboardHelpers";

// Shared section label so "Packages & Vouchers" reads as two clearly named lists.
const SECTION_LABEL_SX = {
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "text.secondary",
  mb: 1
};

function DashboardView() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency } = useWidget();
  const { user, client, refreshUser, activeClient, activeClientId } = useAuth();
  const { filters, setFilter, setFilters } = useUrlFilters({ tab: "overview", scope: "today", sort: "date" });

  // Vouchers and Sports merged into Packages & Vouchers / Preferences — coerce
  // legacy deep links so old bookmarks still land somewhere sensible.
  const activeTab = filters.tab === "vouchers" ? "packages" : filters.tab === "sports" ? "preferences" : filters.tab;

  // Bookings and Invoices share the status/sort URL params; sort is coerced per
  // tab so an option from one tab (e.g. "recent") never shows blank on the other.
  const isBookingsTab = activeTab === "bookings";
  // Today is the default: a session drops out of Upcoming the moment it ends,
  // which is exactly when a client opens the widget to check in.
  const bookingScope = filters.scope || "today";
  const bookingStatus = filters.status || null;
  const bookingSort = filters.sort === "recent" ? "recent" : "date";
  const bookingType = filters.type || null;
  const bookingsNarrowed = bookingScope !== "all" || Boolean(bookingStatus) || Boolean(bookingType);
  const isWaitlistScope = isBookingsTab && bookingScope === "waitlist";

  // An empty Today is the most common empty state there is, so it offers
  // Upcoming rather than dumping the client into their full history.
  const bookingsEmptyState =
    bookingScope === "today" && !bookingStatus && !bookingType
      ? { title: "Nothing today", description: "You have no sessions today.", actionLabel: "View upcoming bookings", actionScope: "upcoming" }
      : bookingsNarrowed
        ? {
            title: "No bookings here",
            description: "Nothing matches the current filters. View all bookings to see your full history.",
            actionLabel: "View all bookings",
            actionScope: "all"
          }
        : { title: "No bookings yet", description: "Bookings you make will show up here." };

  const isInvoicesTab = activeTab === "invoices";
  const invoiceStatus = filters.status || null;
  const invoiceSort = filters.sort === "amount" ? "amount" : "date";
  const invoicesNarrowed = Boolean(invoiceStatus);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((n) => n + 1);

  useEffect(() => {
    if (!user?.profile) refreshUser();
  }, [user, refreshUser]);

  const isOverviewTab = activeTab === "overview";
  const isPreferencesTab = activeTab === "preferences";
  const isPackagesTab = activeTab === "packages";

  // On mobile the tab strip is replaced by an account hub: with no ?tab the
  // dashboard opens on a menu of every section (Overview included), and picking
  // one takes over the screen with a back link to the menu. The default tab is
  // "overview", so menu vs. section is told apart by the raw param's presence.
  const searchParams = new URLSearchParams(location.search);
  const hasTabParam = searchParams.has("tab");
  const showMobileMenu = isMobile && !hasTabParam;
  // A section opened from the menu is its own history entry, so the phone's back
  // button returns to the menu instead of skipping past the whole Account page.
  // The on-screen back link then pops that entry rather than stacking another;
  // a section reached any other way (deep link, desktop resize) has no menu
  // entry beneath it, so it swaps itself for the menu in place.
  const openedFromMenu = location.state?.fromSectionMenu === true;
  const openSection = (tab) => {
    const params = new URLSearchParams(location.search);
    ["scope", "status", "type", "sort"].forEach((key) => params.delete(key));
    params.set("tab", tab);
    navigate({ search: `?${params}` }, { state: { fromSectionMenu: true } });
  };
  const goToMenu = () => (openedFromMenu ? navigate(-1) : setFilters({ tab: null, scope: null, status: null, type: null, sort: null }));
  const inMobileSection = isMobile && !showMobileMenu;
  // When a section is opened from elsewhere (e.g. the booking views' "edit
  // preferences" shortcut) it carries a `from` path. Go back in history rather
  // than pushing it — pushing would leave the origin duplicated on the stack, so
  // its own back arrow would bounce here. Fall back to the path only on a cold
  // deep-link with no history to pop.
  const returnTo = searchParams.get("from");
  const backFrom = () => (location.key && location.key !== "default" ? navigate(-1) : navigate(returnTo));
  const backLabel = inMobileSection ? ACTIVITY_TABS.find((t) => t.value === activeTab)?.label || "Account" : "Back to home";
  const onBack = returnTo ? backFrom : inMobileSection ? goToMenu : () => navigate(`/${slug}`);

  const {
    data: dashboard,
    loading: dashboardLoading,
    error: dashboardError
  } = useFetch(() => api.getUserDashboard(slug), [slug, refreshKey, activeClientId], {
    skip: !slug || !isOverviewTab || showMobileMenu
  });

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [filters.tab, filters.scope, filters.status, filters.type, filters.sort]);

  const { data: activity, loading: activityLoading } = useFetch(
    () =>
      api.getUserActivity(slug, {
        tab: activeTab,
        page,
        limit: PAGE_SIZE,
        ...(isBookingsTab ? { scope: bookingScope, status: bookingStatus, type: bookingType, sort: bookingSort } : {}),
        ...(isInvoicesTab ? { status: invoiceStatus, sort: invoiceSort } : {})
      }),
    [slug, activeTab, page, refreshKey, activeClientId, filters.scope, filters.status, filters.type, filters.sort],
    { skip: !slug || isOverviewTab || isPreferencesTab || isWaitlistScope }
  );
  // Show the full-list spinner whenever the client asked for a different set of
  // rows — a tab switch or a filter/sort pill. Anything else that refetches
  // (auth re-resolving activeClientId, returning from the gateway, pagination)
  // is deliberately absent from this key so it keeps the current list on screen
  // instead of blanking it.
  const activityQueryKey = `${activeTab}|${filters.scope || ""}|${filters.status || ""}|${filters.type || ""}|${filters.sort || ""}`;
  const [loadedQueryKey, setLoadedQueryKey] = useState(null);
  useEffect(() => {
    if (!activityLoading && activity) setLoadedQueryKey(activityQueryKey);
  }, [activityLoading, activity, activityQueryKey]);
  const showListSpinner = activityLoading && loadedQueryKey !== activityQueryKey;

  // The booking types this client has, for the type filter. Kept from the last bookings
  // response so the chips stay put while the next page or filter loads.
  const [bookingTypes, setBookingTypes] = useState([]);
  useEffect(() => {
    if (Array.isArray(activity?.availableTypes)) setBookingTypes(activity.availableTypes);
  }, [activity]);

  // Class waitlists get their own Bookings filter: a client waiting on a session
  // checks there for where they stand, and can step off the list. Fetched on the
  // whole tab so the filter only appears for clients who are actually waiting.
  const [waitlistKey, setWaitlistKey] = useState(0);
  const [leavingWaitlistId, setLeavingWaitlistId] = useState(null);
  const { data: waitlistData, loading: waitlistLoading } = useFetch(() => api.getMyWaitlists(slug), [slug, refreshKey, activeClientId, waitlistKey], {
    skip: !slug || !isBookingsTab || showMobileMenu
  });
  const waitlists = waitlistData?.items || [];
  // A held seat is on a clock, so it leads; then the lists still open, soonest
  // session first; closed ones sink to the bottom as history, latest first.
  const openWaitlists = waitlists
    .filter((w) => !w.closed && !w.started)
    .sort((a, b) => Number(Boolean(b.offer)) - Number(Boolean(a.offer)) || String(a.startDate).localeCompare(String(b.startDate)));
  const closedWaitlists = waitlists.filter((w) => w.closed || w.started).sort((a, b) => String(b.startDate).localeCompare(String(a.startDate)));
  const handleLeaveWaitlist = async (item) => {
    setLeavingWaitlistId(item.entryId);
    try {
      await api.leaveClassWaitlist(slug, item.bookishId);
      enqueueSnackbar("You've left the waitlist", { variant: "info" });
      setWaitlistKey((k) => k + 1);
    } catch (err) {
      enqueueSnackbar(err.message || "Could not leave the waitlist", { variant: "error" });
    } finally {
      setLeavingWaitlistId(null);
    }
  };

  // Vouchers ride along on the Packages & Vouchers tab in a card of their own
  // (unpaginated — a client rarely holds more than a handful).
  const { data: voucherData } = useFetch(
    () => api.getUserActivity(slug, { tab: "vouchers", page: 1, limit: 50 }),
    [slug, refreshKey, activeClientId],
    {
      skip: !slug || !isPackagesTab
    }
  );
  // Sport levels render at the bottom of the Preferences tab.
  const { data: sportsData, loading: sportsLoading } = useFetch(() => api.getUserSports(slug), [slug, refreshKey, activeClientId], {
    skip: !slug || !isPreferencesTab
  });

  const [cancelDialog, setCancelDialog] = useState({ open: false, booking: null, preview: null, loading: false, submitting: false });
  const [payLoadingId, setPayLoadingId] = useState(null);
  const [settleDialog, setSettleDialog] = useState({ open: false, booking: null });
  const [rescheduleDialog, setRescheduleDialog] = useState({ open: false, booking: null });
  // Patches applied to individual booking rows after a fully-covered settle, so
  // one booking flips to Paid in place without reloading (and blanking) the list.
  const [bookingPatches, setBookingPatches] = useState({});
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, invoice: null, loading: false });
  const [checkInDialog, setCheckInDialog] = useState({ open: false, booking: null, submitting: false });

  const openSettleDialog = (booking) => setSettleDialog({ open: true, booking });
  // A private lesson moves on the lessons page and a court booking on the courts page,
  // which already know coaches, courts and times.
  const openRescheduleDialog = (booking) => {
    if (booking.type === "category-service-court") {
      const params = new URLSearchParams({
        reschedule: booking._id,
        date: booking.date,
        serviceId: booking.serviceId,
        duration: String(booking.duration)
      });
      if (booking.locationId) params.set("locationId", booking.locationId);
      navigate(`/${slug}/courts?${params.toString()}`);
      return;
    }
    if (booking.type === "category-service-private") {
      const params = new URLSearchParams({
        reschedule: booking._id,
        date: booking.date,
        serviceId: booking.serviceId,
        duration: String(booking.duration)
      });
      if (booking.trainerId) params.set("trainerId", booking.trainerId);
      if (booking.locationId) params.set("locationId", booking.locationId);
      navigate(`/${slug}/lessons?${params.toString()}`);
      return;
    }
    setRescheduleDialog({ open: true, booking });
  };
  // Keeps the booking: the dialog still renders it while it fades out.
  const closeRescheduleDialog = () => setRescheduleDialog((prev) => ({ ...prev, open: false }));

  // The move kept the new place but couldn't take payment from the old booking's source —
  // the credit is back on the pack or in the wallet, and the new booking shows as unpaid,
  // settled like any other through Pay Now.
  const onRescheduled = ({ result, label }) => {
    closeRescheduleDialog();
    const unpaidCopy =
      result.mode === "MONEY_MOVE"
        ? "Your place is moved and your payment is in your wallet, but we couldn't apply it — please use Pay Now with your wallet on the new booking."
        : "Your place is moved, but we couldn't take the package session — please use Pay Now on the new booking.";
    enqueueSnackbar(result.requiresPayment ? unpaidCopy : `Booking moved to ${label}.`, { variant: result.requiresPayment ? "warning" : "success" });
    refresh();
  };
  const onRescheduleBookingChanged = () => {
    closeRescheduleDialog();
    refresh();
  };
  const closeSettleDialog = () => setSettleDialog({ open: false, booking: null });

  const onBookingSettled = ({ invoiceId }) => {
    if (!settleDialog.booking) return;
    setBookingPatches((prev) => ({ ...prev, [bookingRowKey(settleDialog.booking)]: { invoiceId, invoiceStatus: "PAID" } }));
    closeSettleDialog();
  };

  // Pay Now redirects to the gateway via a full navigation; hitting back restores
  // this page from the bfcache with payLoadingId still set, leaving the button
  // stuck spinning. pageshow fires on that restore — clear the loading state.
  useEffect(() => {
    const onPageShow = () => setPayLoadingId(null);
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Coming back to a backgrounded tab refetches. useCheckInOpen opens the
  // check-in button on a timer, but browsers throttle or defer timers in hidden
  // tabs — this is the safety net for the phone that was locked through the
  // moment the window opened.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const openInvoiceDialog = async (row) => {
    setInvoiceDialog({ open: true, invoice: null, loading: true });
    try {
      const invoice = await api.getInvoice(slug, row._id);
      setInvoiceDialog({ open: true, invoice, loading: false });
    } catch (err) {
      setInvoiceDialog({ open: false, invoice: null, loading: false });
      enqueueSnackbar(err.message || "Failed to load invoice", { variant: "error" });
    }
  };

  const closeInvoiceDialog = () => setInvoiceDialog({ open: false, invoice: null, loading: false });

  const [usageDialog, setUsageDialog] = useState({ open: false, data: null, loading: false, error: null });

  const openUsageDialog = async (pkg) => {
    setUsageDialog({ open: true, data: null, loading: true, error: null });
    try {
      const data = await api.getPlanUsage(slug, pkg._id);
      setUsageDialog({ open: true, data, loading: false, error: null });
    } catch (err) {
      setUsageDialog({ open: true, data: null, loading: false, error: err.message || "Failed to load package usage" });
    }
  };

  const closeUsageDialog = () => setUsageDialog({ open: false, data: null, loading: false, error: null });

  const openCancelDialog = async (booking) => {
    setCancelDialog({ open: true, booking, preview: null, refundMethod: null, loading: true, submitting: false });
    try {
      const preview = await api.getBookingRefundPreview(slug, booking._id);
      const defaultMethod = preview.availableMethods?.[0] || null;
      setCancelDialog((s) => ({ ...s, preview, refundMethod: defaultMethod, loading: false }));
    } catch (err) {
      setCancelDialog({ open: false, booking: null, preview: null, refundMethod: null, loading: false, submitting: false });
      enqueueSnackbar(err.message || "Failed to retrieve cancellation policy", { variant: "error" });
    }
  };

  const closeCancelDialog = () => {
    if (cancelDialog.submitting) return;
    setCancelDialog({ open: false, booking: null, preview: null, refundMethod: null, loading: false, submitting: false });
  };

  const setCancelRefundMethod = (method) => setCancelDialog((s) => ({ ...s, refundMethod: method }));

  const confirmCancel = async () => {
    const booking = cancelDialog.booking;
    if (!booking) return;
    setCancelDialog((s) => ({ ...s, submitting: true }));
    try {
      const result = await api.cancelBooking(slug, booking._id, { refundMethod: cancelDialog.refundMethod });
      const hasMoneyMovement = result.chargeAmount > 0 || result.refundAmount > 0;
      const split = result.splitGame;
      enqueueSnackbar(
        split?.dissolved
          ? `Game cancelled.${
              split.hostChargeAmount > 0
                ? ` ${formatMoney(split.hostChargeAmount, currency)} was charged per the cancellation policy.`
                : split.holdReleased
                  ? " The hold on your card was released."
                  : ""
            }${split.seatsRefunded > 0 ? ` ${split.seatsRefunded} paid player${split.seatsRefunded !== 1 ? "s were" : " was"} refunded.` : ""}`
          : hasMoneyMovement
            ? `Booking cancelled. Charged ${formatMoney(result.chargeAmount, currency)}, refunded ${formatMoney(result.refundAmount, currency)}.`
            : "Booking cancelled.",
        { variant: "success" }
      );
      setCancelDialog({ open: false, booking: null, preview: null, refundMethod: null, loading: false, submitting: false });
      refresh();
    } catch (err) {
      setCancelDialog((s) => ({ ...s, submitting: false }));
      enqueueSnackbar(err.message || "Failed to cancel booking", { variant: "error" });
    }
  };

  const openCheckInDialog = (booking) => setCheckInDialog({ open: true, booking, submitting: false });
  const closeCheckInDialog = () => {
    if (checkInDialog.submitting) return;
    setCheckInDialog({ open: false, booking: null, submitting: false });
  };

  const confirmCheckIn = async () => {
    const booking = checkInDialog.booking;
    if (!booking) return;
    setCheckInDialog((s) => ({ ...s, submitting: true }));
    try {
      const result = await api.checkInBooking(slug, booking._id);
      // Patch the row so it flips in place instead of blanking the list.
      // checkInWindow is cleared alongside: it means "eligible, pending the
      // clock", and a checked-in seat is no longer eligible. The Overview card
      // has no patch layer, so that one takes a refetch.
      setBookingPatches((prev) => ({
        ...prev,
        [bookingRowKey(booking)]: { ...(prev[bookingRowKey(booking)] || {}), checkIn: result.checkIn, checkInWindow: null }
      }));
      setCheckInDialog({ open: false, booking: null, submitting: false });
      enqueueSnackbar(result.alreadyCheckedIn ? "You were already checked in." : "You're checked in.", { variant: "success" });
      if (isOverviewTab) refresh();
    } catch (err) {
      setCheckInDialog((s) => ({ ...s, submitting: false }));
      enqueueSnackbar(err.message || "Failed to check in", { variant: "error" });
    }
  };

  // bookingId is only passed for bookings, which may have no invoice yet: the
  // club can create a booking from the scheduler without checking it out, and
  // there's nothing to pay against until one is minted.
  const handlePayNow = async (invoiceId, rowId, bookingId) => {
    if (!invoiceId && !bookingId) return;
    setPayLoadingId(rowId);
    try {
      const payableInvoiceId = invoiceId || (await api.createBookingInvoice(slug, bookingId)).invoiceId;
      if (!payableInvoiceId) {
        enqueueSnackbar("No payment link available for this booking", { variant: "error" });
        setPayLoadingId(null);
        return;
      }
      const { chargeId } = await api.getInvoicePayLink(slug, payableInvoiceId);
      if (!chargeId) {
        enqueueSnackbar("No payment link available for this invoice", { variant: "error" });
        setPayLoadingId(null);
        return;
      }
      // Soft SPA navigation (same as CheckoutView) — a hard window.location reload
      // would white-flash the whole app and reload it again on back.
      navigate(`/checkout/telr/${chargeId}?clubId=${slug}`);
    } catch (err) {
      setPayLoadingId(null);
      enqueueSnackbar(err.message || "Failed to resume payment", { variant: "error" });
    }
  };

  const profile = user?.profile || {};
  const firstName = activeClient?.firstName || client?.firstName || profile.firstName || "";
  const lastName = activeClient?.lastName || client?.lastName || profile.lastName || "";
  const email = client?.email || user?.email || profile.email || "";

  // The club record only, with no fall back to the account profile: that number
  // is what every reminder dials, so showing the account's instead would hide an
  // empty club record behind a number nobody is sending to.
  const phone = client?.phone || "";

  // The photo belongs to whoever the user is acting as, so a parent booking for
  // a child must not fall back to their own photo when the child has none.
  const imgUrl = (activeClient || client)?.imgUrl || null;

  // The tab's list and its pager. On Packages & Vouchers it folds away, so a
  // long list of packages doesn't push the vouchers card out of reach.
  const activityList = (
    <>
      <Box>
        <ActivityList
          tab={activeTab}
          items={(activity?.items || []).map((it) => (bookingPatches[bookingRowKey(it)] ? { ...it, ...bookingPatches[bookingRowKey(it)] } : it))}
          currency={currency}
          onCancel={openCancelDialog}
          onReschedule={openRescheduleDialog}
          onPayNow={handlePayNow}
          onSettleBooking={openSettleDialog}
          onCheckIn={openCheckInDialog}
          payLoadingId={payLoadingId}
          onViewInvoice={openInvoiceDialog}
          onViewUsage={openUsageDialog}
          onViewGame={(b) => navigate(`/${slug}/bookings/${b._id}`)}
          onViewClass={(b) => navigate(`/${slug}/classes/${b._id}`)}
        />
      </Box>
      {activity?.pagination?.totalPages > 1 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="textSecondary">
            Showing {(activity.pagination.page - 1) * activity.pagination.limit + 1}–
            {Math.min(activity.pagination.page * activity.pagination.limit, activity.pagination.total)} of {activity.pagination.total}
          </Typography>
          <Pagination
            size="small"
            color="primary"
            count={activity.pagination.totalPages}
            page={activity.pagination.page}
            onChange={(_, p) => setPage(p)}
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}
    </>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
        <IconButton size="small" onClick={onBack} aria-label={backLabel} sx={{ ml: -0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "text.secondary" }}>{backLabel}</Typography>
      </Box>
      <Card>
        {!isMobile && (
          <UrlTabs
            value={activeTab}
            onChange={(v) => setFilters({ tab: v, scope: null, status: null, type: null, sort: null })}
            tabs={ACTIVITY_TABS}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          />
        )}
        {showMobileMenu ? (
          <SectionMenu onSelect={openSection} />
        ) : (
          <CardContent>
            {isOverviewTab ? (
              <OverviewTab
                firstName={firstName}
                lastName={lastName}
                email={email}
                phone={phone}
                phoneEditable={!!client?.phoneEditable}
                imgUrl={imgUrl}
                onAvatarUpdated={refreshUser}
                onPhoneUpdated={refreshUser}
                dashboard={dashboard}
                loading={dashboardLoading}
                error={dashboardError}
                onCheckIn={openCheckInDialog}
              />
            ) : isPreferencesTab ? (
              <>
                <PreferencesTab slug={slug} reloadKey={`${refreshKey}-${activeClientId}`} onSaved={refresh} />
                <Box mt={4}>
                  <Typography variant="h6" gutterBottom>
                    Sports & Levels
                  </Typography>
                  <SportsList items={sportsData?.items || []} loading={sportsLoading} />
                </Box>
              </>
            ) : (
              <>
                {isBookingsTab && (
                  <BookingFilters
                    scope={bookingScope}
                    status={bookingStatus}
                    sort={bookingSort}
                    type={bookingType}
                    availableTypes={bookingTypes}
                    onScopeChange={(v) =>
                      v === "waitlist" ? setFilters({ scope: v, status: null, type: null }) : setFilter("scope", v || "upcoming")
                    }
                    hasWaitlists={waitlists.length > 0}
                    onStatusChange={(v) => setFilter("status", v)}
                    onTypeChange={(v) => setFilter("type", v)}
                    onSortChange={(v) => setFilter("sort", v)}
                  />
                )}
                {isInvoicesTab && (
                  <InvoiceFilters
                    status={invoiceStatus}
                    sort={invoiceSort}
                    onStatusChange={(v) => setFilter("status", v)}
                    onSortChange={(v) => setFilter("sort", v)}
                  />
                )}
                {isWaitlistScope ? (
                  waitlistLoading && !waitlistData ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : waitlists.length === 0 ? (
                    <EmptyState
                      title="You're not on any waitlist"
                      description="When a class you want is full, join its waitlist and it will show up here."
                      actionLabel="View upcoming bookings"
                      onAction={() => setFilters({ scope: "upcoming", status: null })}
                    />
                  ) : (
                    <>
                      {openWaitlists.length > 0 && (
                        <Box>
                          {openWaitlists.map((item) => (
                            <WaitlistRow
                              key={item.entryId}
                              item={item}
                              onOpen={(it) => navigate(`/${slug}/classes/${it.bookishId}`)}
                              onLeave={handleLeaveWaitlist}
                              leaving={leavingWaitlistId === item.entryId}
                            />
                          ))}
                        </Box>
                      )}
                      {closedWaitlists.length > 0 && (
                        <Box
                          mt={openWaitlists.length > 0 ? 3 : 0}
                          pt={openWaitlists.length > 0 ? 2.5 : 0}
                          sx={{ borderTop: openWaitlists.length > 0 ? 1 : 0, borderColor: "divider" }}
                        >
                          <Typography sx={SECTION_LABEL_SX}>Closed</Typography>
                          {closedWaitlists.map((item) => (
                            <WaitlistRow
                              key={item.entryId}
                              item={item}
                              onOpen={(it) => navigate(`/${slug}/classes/${it.bookishId}`)}
                              onLeave={handleLeaveWaitlist}
                              leaving={leavingWaitlistId === item.entryId}
                            />
                          ))}
                        </Box>
                      )}
                    </>
                  )
                ) : showListSpinner ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                ) : isBookingsTab && !(activity?.items || []).length ? (
                  <EmptyState
                    title={bookingsEmptyState.title}
                    description={bookingsEmptyState.description}
                    actionLabel={bookingsEmptyState.actionLabel}
                    onAction={
                      bookingsEmptyState.actionScope
                        ? () => setFilters({ scope: bookingsEmptyState.actionScope, status: null, type: null })
                        : undefined
                    }
                  />
                ) : isInvoicesTab && !(activity?.items || []).length ? (
                  <EmptyState
                    title={invoicesNarrowed ? "No invoices here" : "No invoices yet"}
                    description={
                      invoicesNarrowed
                        ? "Nothing matches the current filters. View all invoices to see your full history."
                        : "Invoices will appear here once you make a purchase."
                    }
                    actionLabel={invoicesNarrowed ? "View all invoices" : undefined}
                    onAction={invoicesNarrowed ? () => setFilters({ status: null }) : undefined}
                  />
                ) : (
                  <>
                    {isPackagesTab ? (
                      <CollapsibleSection
                        title="Packages"
                        count={activity?.pagination?.total ?? (activity?.items || []).length}
                        labelSx={SECTION_LABEL_SX}
                      >
                        {activityList}
                      </CollapsibleSection>
                    ) : (
                      activityList
                    )}
                  </>
                )}
              </>
            )}
          </CardContent>
        )}
      </Card>

      {isPackagesTab && !showMobileMenu && (voucherData?.items || []).length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <CollapsibleSection title="Vouchers" count={voucherData.items.length} labelSx={SECTION_LABEL_SX}>
              <ActivityList tab="vouchers" items={voucherData.items} currency={currency} onPayNow={handlePayNow} payLoadingId={payLoadingId} />
            </CollapsibleSection>
          </CardContent>
        </Card>
      )}

      {/* Under the phone's section menu only, never inside a section: the second of
          the two places (with the home band) where the app is offered. */}
      {showMobileMenu && <InstallAppCard sx={{ mt: 2, mb: 0 }} />}
      {showMobileMenu && <InviteQrCard sx={{ mt: 2 }} />}

      <CancelBookingDialog
        state={cancelDialog}
        currency={currency}
        onClose={closeCancelDialog}
        onConfirm={confirmCancel}
        onRefundMethodChange={setCancelRefundMethod}
      />
      <InvoiceDetailsDialog
        state={invoiceDialog}
        currency={currency}
        onClose={closeInvoiceDialog}
        onPayNow={(invoiceId) => {
          closeInvoiceDialog();
          handlePayNow(invoiceId, invoiceId);
        }}
        payLoadingId={payLoadingId}
      />
      <CheckInDialog state={checkInDialog} onClose={closeCheckInDialog} onConfirm={confirmCheckIn} />
      <PlanUsageDialog state={usageDialog} onClose={closeUsageDialog} />
      <RescheduleDialog
        open={rescheduleDialog.open}
        booking={rescheduleDialog.booking}
        slug={slug}
        currency={currency}
        onClose={closeRescheduleDialog}
        onRescheduled={onRescheduled}
        onBookingChanged={onRescheduleBookingChanged}
      />
      <SettlePaymentDialog
        open={settleDialog.open}
        booking={settleDialog.booking}
        slug={slug}
        currency={currency}
        onClose={closeSettleDialog}
        onPaid={onBookingSettled}
      />
    </Box>
  );
}

export default DashboardView;
